import React from "react";
import {
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

// MUI icons for bullets
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import TableChartIcon from "@mui/icons-material/TableChart";
import TuneIcon from "@mui/icons-material/Tune";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LinkIcon from "@mui/icons-material/Link";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import InsightsIcon from "@mui/icons-material/Insights";
import TimelineIcon from "@mui/icons-material/Timeline";

import { Helmet } from "react-helmet-async";

// ---------- small building blocks ----------
const Bullet = ({ icon, title, text, color = "#6C5CE7" }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: 2,
        display: "grid",
        placeItems: "center",
        bgcolor: color + "22",
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Stack spacing={0.5}>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      <Typography color="text.secondary">{text}</Typography>
      {/* optional learn-more link slot per bullet (kept simple) */}
    </Stack>
  </Stack>
);

const SectionRow = ({
  eyebrow,
  title,
  lead,
  bullets,
  imageSrc,
  imageAlt,
  imageLeft = false,
  cta, // {to,label,variant}
}) => (
  <Box sx={{ py: { xs: 6, md: 10 } }}>
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 6 } }}>
      <Grid
        container
        spacing={{ xs: 4, md: 8 }}
        alignItems="center"
        direction={imageLeft ? "row-reverse" : "row"}
      >
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {eyebrow && (
              <Typography variant="overline" color="primary">
                {eyebrow}
              </Typography>
            )}
            <Typography variant="h3" fontWeight={800} lineHeight={1.1}>
              {title}
            </Typography>
            {lead && (
              <Typography color="text.secondary" sx={{ fontSize: 18 }}>
                {lead}
              </Typography>
            )}
            <Stack spacing={3} sx={{ pt: 1 }}>
              {bullets.map((b) => (
                <Bullet key={b.title} {...b} />
              ))}
            </Stack>

            {cta && (
              <Stack direction="row" spacing={1.5} sx={{ pt: 2 }}>
                <Button
                  component={RouterLink}
                  to={cta.to}
                  variant={cta.variant || "contained"}
                  size="large"
                >
                  {cta.label}
                </Button>
              </Stack>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid #E7ECF5",
              overflow: "hidden",
              background:
                imageSrc
                  ? "#fff"
                  : "linear-gradient(180deg, #F6F8FF 0%, #FFFFFF 100%)",
            }}
          >
            {imageSrc ? (
              <CardMedia
                component="img"
                image={imageSrc}
                alt={imageAlt || title}
                sx={{ display: "block" }}
              />
            ) : (
              <Box sx={{ p: 6 }}>
                <Typography color="text.secondary">
                  (Add an illustrative screenshot at{" "}
                  <code>public/images/…</code>)
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

// ---------- page ----------
export default function ProjectManagement() {
  return (
    <Box>

      <Helmet>
  <title>Project Planning Software for CA Firms | WorkPulse.in</title>

  <meta
    name="description"
    content="Create entity-wise projects, assign owners, set due dates and priorities, track progress and roll up insights across clients—built for CA firms."
  />

  <link rel="canonical" href="https://workpulse.in/project-management" />

  {/* OpenGraph */}
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="WorkPulse.in" />
  <meta property="og:url" content="https://workpulse.in/" />
  <meta property="og:title" content="Never lose track of client work" />
  <meta
    property="og:description"
    content="Entity-wise workflow, project management, timesheets and activity trail in one secure workspace. Built for Chartered Accountants in India."
  />
  <meta property="og:image" content="https://workpulse.in/og-workpulse.png" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />

  {/* Twitter (works even without a Twitter account) */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Never lose track of client work" />
  <meta
    name="twitter:description"
    content="Entity-wise workflow, project management, timesheets and activity trail in one secure workspace."
  />
  <meta name="twitter:image" content="https://workpulse.in/og-workpulse.png" />
</Helmet>



      {/* HERO */}
      

      {/* SECTION 1 — Project Planning */}
      <SectionRow
        eyebrow="Project Planning"
        title="Organize projects the way your business runs"
        lead="Create projects under entities, assign managers and team leaders, add due dates and priorities, and keep work structured with Areas/Processes."
        bullets={[
          {
            icon: <CalendarMonthIcon />,
            color: "#6C5CE7",
            title: "Advanced planning tools",
            text:
              "Plan by entity and category, set stages and priorities, and keep dates visible across the portfolio.",
          },
          {
            icon: <Inventory2Icon />,
            color: "#E0467B",
            title: "Ready for complex portfolios",
            text:
              "Scale from a handful of projects to hundreds with robust filtering by entity, manager, status and category.",
          },
          {
            icon: <TaskAltIcon />,
            color: "#10B981",
            title: "Beyond dates & scope",
            text:
              "Tie work to Areas/Processes so tasks, time and insights always roll up to the right swim-lane.",
          },
        ]}
        imageSrc={`${process.env.PUBLIC_URL}/images/pm-planning.png`}
        imageAlt="WorkPulse projects list & details"
        cta={{ to: "/projects", label: "Go to Projects" }}
      />

      {/* SECTION 2 — Task Management */}
      <SectionRow
        imageLeft
        eyebrow="Task Management"
        title="Keep every task owned, prioritized and moving"
        lead="Fast task entry and powerful filters give managers and members complete clarity across projects."
        bullets={[
          {
            icon: <TableChartIcon />,
            color: "#3B82F6",
            title: "Multiple views that stay in sync",
            text:
              "Clean data tables with quick filters by entity, project, assignee, status, priority and due dates.",
          },
          {
            icon: <ViewKanbanIcon />,
            color: "#10B981",
            title: "Your tasks, your way",
            text:
              "Group by entity or project, narrow to owner, or focus on a single status—whatever you need right now.",
          },
          {
            icon: <TuneIcon />,
            color: "#8B5CF6",
            title: "Bulk & essentials covered",
            text:
              "Add via dialog or CSV upload, edit inline, change status/priority, and track progress with one click.",
          },
        ]}
        imageSrc={`${process.env.PUBLIC_URL}/images/pm-tasks.png`}
        imageAlt="WorkPulse tasks table"
        cta={{ to: "/tasks", label: "Open Tasks", variant: "outlined" }}
      />

      {/* SECTION 3 — Time Tracking */}
      <SectionRow
        eyebrow="Time Tracking"
        title="Timesheets that match with what work is done"
        lead="Weekly timesheets map hours to the exact project and area, so reporting is clean and reliable."
        bullets={[
          {
            icon: <AccessTimeIcon />,
            color: "#10B981",
            title: "My Time",
            text:
              "Review your activity and log hours for the week with a compact, keyboard-friendly grid.",
          },
          {
            icon: <LinkIcon />,
            color: "#3B82F6",
            title: "Integrated reporting",
            text:
              "Hours link directly to Projects and Areas/Processes. Managers can filter by user, project and date.",
          },
          {
            icon: <QueryStatsIcon />,
            color: "#F59E0B",
            title: "Analytics & exports",
            text:
              "Monthly rollups and pivot-style matrices by user, project and area—great for utilization and billing.",
          },
        ]}
        imageSrc={`${process.env.PUBLIC_URL}/images/pm-time.png`}
        imageAlt="WorkPulse timesheet grid"
        cta={{ to: "/timesheet", label: "Enter Time" }}
      />

      {/* SECTION 4 — Progress Tracking & Analytics */}
      <SectionRow
        imageLeft
        eyebrow="Progress Tracking, Reports & Analytics"
        title="From overview to drill-down in a click"
        lead="Dashboards and Insights turn your work data into clear decisions—by entity, project, user and process."
        bullets={[
          {
            icon: <DashboardCustomizeIcon />,
            color: "#10B981",
            title: "Automated portfolio reports",
            text:
              "See totals for entities, projects, users and tasks on the dashboard—tap any tile to drill into detail.",
          },
          {
            icon: <InsightsIcon />,
            color: "#3B82F6",
            title: "Advanced analytics",
            text:
              "Timesheet Insights provide monthly trends and project×user matrices to spot bottlenecks early.",
          },
          {
            icon: <TimelineIcon />,
            color: "#8B5CF6",
            title: "Progress tracking views",
            text:
              "Project Information shows status, deadlines and stage progression—perfect for review meetings.",
          },
        ]}
        imageSrc={`${process.env.PUBLIC_URL}/images/pm-analytics.png`}
        imageAlt="WorkPulse insights & analytics"
        cta={{ to: "/timesheet-insights", label: "Open Insights", variant: "outlined" }}
      />

      {/* FINAL CTA */}
      <Box sx={{ py: { xs: 6, md: 10 }, background: "linear-gradient(#F8FAFF, #FFFFFF)" }}>
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography variant="h4" fontWeight={900}>
              Ready to streamline project execution?
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
              Start with Projects and Tasks, then add Timesheets and Insights when you’re ready.
              Role-based access ensures everyone sees exactly what they need.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1 }}>
              <Button component={RouterLink} to="/projects" size="large" variant="contained">
                Open Projects
              </Button>
              <Button component={RouterLink} to="/tasks" size="large" variant="outlined">
                Browse Tasks
              </Button>
              <Button component={RouterLink} to="/activity-log" size="large" variant="text">
                View Activity Log
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
